import { useState, useEffect, useCallback } from "react";
import { readListCache, writeListCache, userListScope } from "@/utils/listCache";
import { useNavigate, useLocation } from "react-router-dom";
import {
  IonCard, IonCardHeader, IonCardTitle, IonList, IonItem, IonItemGroup, IonItemDivider,
  IonLabel, IonButton, IonIcon, IonSpinner, IonContent, IonToggle, IonReorderGroup, IonReorder,
} from "@ionic/react";
import {
  addOutline, createOutline, trashOutline, copyOutline, cloudUploadOutline, cloudOfflineOutline, openOutline,
} from "ionicons/icons";
import AppLayout from "@/components/AppLayout";
import AppSeo from "@/components/AppSeo";
import RowActions from "@/components/RowActions";
import { useSavedAssets } from "@/components/SavedAssets";
import { isUserLoggedIn, userHeaders } from "@/utils/userAuth";
import { useAppLive } from "@/utils/appLive";
import { confirmAlert } from "@/utils/confirmAlert";
import { toast } from "@/utils/toast";
import { clearLayoutCache, STARTER_LAYOUTS } from "@/utils/layoutEngine";
import FreshPopover from "@/components/FreshPopover";
import { useDisabledGenerators, LEGAL_GENERATOR_IDS, TAX_GENERATOR_IDS, BUSINESS_GENERATOR_IDS } from "@/utils/generatorAvailability";

// The Create menu's built-in entries, with the ids the app layout uses and
// the admin switch that removes each one
const CREATE_BUILT_INS = [
  { id: "paystub", label: "Pay Stub", off: (d) => d.has("paystub") },
  { id: "canadian-paystub", label: "Canadian Paystub", off: (d) => d.has("canadian-paystub") },
  { id: "uk-payslip", label: "UK Payslip", off: (d) => d.has("uk-payslip") },
  { id: "offer-letter", label: "Offer Letter", off: (d) => d.has("offer-letter") },
  { id: "bank-statement", label: "Accounting Mockup", off: (d) => d.has("bank-statement") },
  { id: "ai-resume", label: "AI Resume", off: (d) => d.has("ai-resume") },
  { id: "tax-forms", label: "Tax Forms", off: (d) => TAX_GENERATOR_IDS.every((g) => d.has(g)) },
  { id: "legal-forms", label: "Legal Forms", off: (d) => LEGAL_GENERATOR_IDS.every((g) => d.has(g)) },
  { id: "business-forms", label: "Business Forms", off: (d) => BUSINESS_GENERATOR_IDS.every((g) => d.has(g)) },
];

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

const BADGE = {
  published: ["admin-badge-green", "Published"],
  draft: ["admin-badge-slate", "Draft"],
};

const DOC_TYPE_LABELS = {
  paystub: "Pay Stub", "canadian-paystub": "Canadian Pay Stub", "uk-payslip": "UK Payslip", "offer-letter": "Offer Letter",
  "legal-document": "Legal Document", resume: "Resume", "bank-statement": "Accounting Mockup", blank: "Blank",
};
// The built-in categories a template can be filed under (the editor's picker
// names), for the row subtitle.
const BUILT_IN_CATEGORY = {
  paystub: "Pay Stubs", "canadian-paystub": "Canadian Paystubs", "uk-payslip": "UK Payslips", "legal-document": "Legal Documents",
  "offer-letter": "Offer Letters", "bank-statement": "Business / Accounting", resume: "Resumes",
  "commercial-lease": "Business / Commercial Leases", "utility-bill": "Business / Utility Bills",
};
// A form generator's design files under that generator's category
const CATEGORY_FOR_BASE = {
  "cease-and-desist": "legal-document", "power-of-attorney": "legal-document", "vehicle-bill-of-sale": "legal-document",
};
// The category a new template starts in: the built-in category of the
// generator it was designed from, so once published it shows up in that
// generator (a pay-stub design joins the Pay Stubs style picker; legal,
// resume and business designs get a card on their generator's page). Offer
// letters have no page of their own, so those get a category of the
// business's own, "My Offer Letters", which becomes a topbar tab and a Create
// item once published. The editor's picker can move any of them.
const AUTO_CATEGORY = { "offer-letter": "My Offer Letters" };
// Where a published template is used, for the row's Open button
const GENERATOR_PAGE = {
  paystub: "/app/paystubs", "canadian-paystub": "/app/canadian-paystub", "uk-payslip": "/app/uk-payslips", "legal-document": "/app/legal-forms",
  resume: "/app/resumes", "bank-statement": "/app/business-forms", "commercial-lease": "/app/business-forms", "utility-bill": "/app/business-forms",
};

// The page's segment row in the top bar (the Signatures pattern): the
// templates, and the categories they are filed under. ?segment=categories
// opens the second.
const SEGMENTS = [
  { value: "templates", label: "Templates", to: "/app/templates" },
  { value: "categories", label: "Categories", to: "/app/templates?segment=categories" },
];

// /app/templates: a business builds its own document templates on the same
// layout engine (and full editor, with the AI assistant) the admin uses. A
// published template's category becomes a tab in this business's topbar and
// an item in its Create menu, and several templates in one category are
// switchable styles of the same document. Pro/Enterprise.
export default function AppTemplates() {
  const navigate = useNavigate();
  const location = useLocation();
  const segment = new URLSearchParams(location.search).get("segment") === "categories" ? "categories" : "templates";
  const { hasAccess, loading: accessLoading } = useSavedAssets();
  const [cats, setCats] = useState([]);
  const [menu, setMenu] = useState({ order: [], hidden: [] }); // the Create menu's order and hidden built-ins
  const [templates, setTemplates] = useState(() => readListCache("templates", userListScope())?.templates ?? null); // the last rows paint at once
  const [busy, setBusy] = useState(false);
  const [rowBusy, setRowBusy] = useState("");
  const [starterMenu, setStarterMenu] = useState({ open: false, event: undefined });
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768); // phone rows fold their links into one menu
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Only the built-in generators are offered to start from (a blank canvas
  // is not: every template must come out of a generator's design). A design
  // the admin has switched off is not offered either.
  const disabledGenerators = useDisabledGenerators();
  const starterOff = (st) => {
    if (st.documentType === "blank") return true;
    if (disabledGenerators.has(st.key)) return true;              // a specific style (e.g. paystub-gusto)
    const byType = {
      "paystub": ["paystub"], "canadian-paystub": ["canadian-paystub"], "uk-payslip": ["uk-payslip"],
      "offer-letter": ["offer-letter"], "resume": ["ai-resume"], "bank-statement": ["bank-statement"],
    }[st.documentType];
    if (byType) return byType.some((id) => disabledGenerators.has(id));
    if (st.documentType === "legal-document") return LEGAL_GENERATOR_IDS.every((id) => disabledGenerators.has(id));
    return false;
  };
  const availableStarters = STARTER_LAYOUTS.filter((st) => !starterOff(st));

  const load = useCallback(async () => {
    if (!isUserLoggedIn()) { setTemplates([]); return; }
    try {
      const [tRes, cRes, mRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/user/doc-templates`, { headers: userHeaders() }),
        fetch(`${BACKEND_URL}/api/user/doc-template-categories`, { headers: userHeaders() }),
        fetch(`${BACKEND_URL}/api/user/create-menu`, { headers: userHeaders() }),
      ]);
      const t = await tRes.json().catch(() => ({}));
      const c = await cRes.json().catch(() => ({}));
      const m = await mRes.json().catch(() => ({}));
      setTemplates(t.templates || []);
      writeListCache("templates", { templates: t.templates || [] }, userListScope());
      setCats(c.categories || []);
      setMenu({ order: m.order || [], hidden: m.hidden || [] });
    } catch { setTemplates((p) => p || []); }
  }, []);
  useEffect(() => { load(); }, [load]);
  useAppLive(load);

  useEffect(() => {
    if (!accessLoading && !hasAccess) navigate("/app/billing", { replace: true });
  }, [accessLoading, hasAccess, navigate]);

  const catLabel = (slug) => (cats.find((c) => c.slug === slug) || {}).label || BUILT_IN_CATEGORY[slug] || (slug === "blank" ? "Unassigned" : slug);

  // see AUTO_CATEGORY: the generator's built-in category, or the business's
  // own category for it (created on first use; the server hands back the
  // existing one after that)
  const categoryFor = async (starter) => {
    const base = starter.documentType || "";
    const label = AUTO_CATEGORY[base];
    if (!label) return CATEGORY_FOR_BASE[base] || base || "blank";
    const res = await fetch(`${BACKEND_URL}/api/user/doc-template-categories`, {
      method: "POST", headers: userHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify({ label }),
    });
    const d = await res.json().catch(() => ({}));
    if (!res.ok || !d.category || !d.category.slug) throw new Error(d.detail || "Could not create the category");
    if (d.categories) setCats(d.categories);
    return d.category.slug;
  };

  // Base a new template on one of the built-in generators' designs (the same
  // starters the admin offers): a Gusto stub, an offer letter, a resume, etc.
  const createFromStarter = async (starter) => {
    setStarterMenu({ open: false, event: undefined });
    setBusy(true);
    try {
      const documentType = await categoryFor(starter);
      const res = await fetch(`${BACKEND_URL}/api/user/doc-templates`, {
        method: "POST", headers: userHeaders({ "Content-Type": "application/json" }),
        // the design comes from the starter and the category from the
        // generator it belongs to (see categoryFor); baseDocType records which
        // built-in the design came from, so the template is filled through
        // that generator's own form.
        body: JSON.stringify({ name: `New ${starter.name}`, description: starter.description || "", documentType, layout: { ...starter.layout, meta: { ...(starter.layout && starter.layout.meta), baseDocType: starter.documentType || "" } }, baseDocType: starter.documentType || "" }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok || d.success === false) throw new Error(d.detail || "Could not create");
      navigate(`/app/templates/edit/${d.template.id}`);
    } catch (err) { toast.error(err.message); } finally { setBusy(false); }
  };

  const rowAction = async (id, path, method = "POST") => {
    setRowBusy(id);
    try {
      const res = await fetch(`${BACKEND_URL}/api/user/doc-templates/${id}${path}`, { method, headers: userHeaders() });
      const d = await res.json().catch(() => ({}));
      if (!res.ok || d.success === false) throw new Error(d.detail || "Action failed");
      clearLayoutCache();
      load();
      return true;
    } catch (err) { toast.error(err.message); return false; } finally { setRowBusy(""); }
  };

  const publishRow = (t) => rowAction(t.id, "/publish").then((ok) => ok && toast.success(`${t.name} published`));
  const unpublishRow = (t) => rowAction(t.id, "/unpublish").then((ok) => ok && toast.success("Unpublished"));
  const duplicateRow = (t) => rowAction(t.id, "/duplicate").then((ok) => ok && toast.success("Duplicated"));
  const deleteRow = async (t) => {
    if (!(await confirmAlert({ header: "Delete this template?", message: `${t.name} is removed for good.`, confirmText: "Delete" }))) return;
    if (await rowAction(t.id, "", "DELETE")) toast.success("Deleted");
  };

  const templateCount = (slug) => templates.filter((t) => t.documentType === slug).length;
  const toggleActionSheet = async (c, on) => {
    setCats((list) => list.map((x) => (x.slug === c.slug ? { ...x, inActionSheet: on } : x)));
    try {
      const res = await fetch(`${BACKEND_URL}/api/user/doc-template-categories/${encodeURIComponent(c.slug)}`, {
        method: "PUT", headers: userHeaders({ "Content-Type": "application/json" }),
        body: JSON.stringify({ inActionSheet: on }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok || d.success === false) throw new Error(d.detail || "Could not update");
      if (d.categories) setCats(d.categories);
    } catch (err) { toast.error(err.message); load(); }
  };
  // The Create menu as the app shows it: the built-ins the admin has on,
  // then the business's categories, in the business's order (anything it has
  // not placed keeps its default spot after).
  const menuItems = (() => {
    const items = [
      ...CREATE_BUILT_INS.filter((b) => !b.off(disabledGenerators)).map((b) => ({ id: b.id, label: b.label, kind: "builtin", shown: !(menu.hidden || []).includes(b.id) })),
      ...cats.map((c) => ({ id: c.slug, label: c.label, kind: "category", shown: c.inActionSheet !== false, cat: c })),
    ];
    const order = menu.order || [];
    const rank = (it, i) => { const k = order.indexOf(it.id); return k === -1 ? order.length + i : k; };
    return items.map((it, i) => [it, rank(it, i)]).sort((a, b) => a[1] - b[1]).map(([it]) => it);
  })();
  const saveMenu = async (next) => {
    setMenu(next);
    try {
      const res = await fetch(`${BACKEND_URL}/api/user/create-menu`, {
        method: "PUT", headers: userHeaders({ "Content-Type": "application/json" }),
        body: JSON.stringify(next),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok || d.success === false) throw new Error(d.detail || "Could not save the Create menu");
      window.dispatchEvent(new Event("ms:create-menu-changed")); // the app's Create button follows
    } catch (err) { toast.error(err.message); load(); }
  };
  const reorderMenu = (e) => {
    const next = e.detail.complete([...menuItems]); // Ionic hands back the list in its new order
    saveMenu({ ...menu, order: next.map((it) => it.id) });
  };
  const toggleMenuItem = (it, on) => {
    if (it.kind === "category") { toggleActionSheet(it.cat, on).then(() => window.dispatchEvent(new Event("ms:create-menu-changed"))); return; }
    const hidden = (menu.hidden || []).filter((id) => id !== it.id);
    saveMenu({ ...menu, hidden: on ? hidden : [...hidden, it.id] });
  };

  const deleteCategory = async (c) => {
    const n = templateCount(c.slug);
    if (!(await confirmAlert({
      header: "Delete this category?",
      message: `${c.label} is removed from your topbar${n ? ` along with its ${n} template${n === 1 ? "" : "s"}` : ""}. This cannot be undone.`,
      confirmText: "Delete",
    }))) return;
    setRowBusy(c.slug);
    try {
      const res = await fetch(`${BACKEND_URL}/api/user/doc-template-categories/${encodeURIComponent(c.slug)}`, { method: "DELETE", headers: userHeaders() });
      const d = await res.json().catch(() => ({}));
      if (!res.ok || d.success === false) throw new Error(d.detail || "Could not delete");
      clearLayoutCache();
      toast.success(`${c.label} deleted`);
      load();
    } catch (err) { toast.error(err.message); } finally { setRowBusy(""); }
  };

  return (
    <AppLayout
      pageTabs={{
        value: segment,
        items: SEGMENTS,
        // replace, not push: segment hops stay one history entry
        onChange: (v) => { const sg = SEGMENTS.find((x) => x.value === v); if (sg) navigate(sg.to, { replace: true }); },
      }}
    >
      <AppSeo title="Templates" description="Your business's own document templates." path="/app/templates" />
      {templates === null || accessLoading ? (
        <div style={{ display: "flex", justifyContent: "center", padding: 60 }}><IonSpinner name="crescent" /></div>
      ) : (
        <div className="admin-profile admin-moderators app-templates" style={{ maxWidth: 1200, margin: "0 auto", paddingBottom: 40 }}>
          {segment === "templates" && (
          <IonCard className="mods-card">
            <IonCardHeader>
              <IonCardTitle className="type-title">
                <span>Document Templates</span>
                <IonButton size="small" fill="outline" disabled={busy} onClick={(e) => setStarterMenu({ open: true, event: e.nativeEvent })}
                  style={{ "--color": "var(--ion-text-color, #0d1b2a)", "--border-color": "var(--ion-text-color, #0d1b2a)" }}>
                  <IonIcon slot="start" icon={addOutline} /> New template
                </IonButton>
              </IonCardTitle>
            </IonCardHeader>
            <IonList>
              <IonItemGroup>
                <IonItemDivider className="border-top">
                  <IonLabel>Your templates</IonLabel>
                  <span slot="end" className="mods-count">{templates.length}</span>
                </IonItemDivider>
                {templates.length === 0 ? (
                  <IonItem lines="none"><IonLabel color="medium">No templates yet. Create one, design it in the editor, assign it a category, and publish it to add that category to your topbar.</IonLabel></IonItem>
                ) : templates.map((t, idx) => {
                  const [cls, text] = BADGE[t.status] || ["admin-badge-slate", t.status];
                  return (
                    <IonItem key={t.id} lines={idx === templates.length - 1 ? "none" : undefined} className="mod-row tpl-row" button detail={false} onClick={() => navigate(`/app/templates/edit/${t.id}`)}>
                      <span slot="start" className="tpl-color-dot" style={{ background: t.badgeColor || "#34d399" }} aria-hidden="true" />
                      <IonLabel>
                        {t.name}
                        <p>{catLabel(t.documentType)}{t.formGroup ? ` · ${t.formGroup}` : ""}</p>
                      </IonLabel>
                      <span slot="end" className={`admin-badge ${cls}`}>{text}</span>
                      {/* the row's links: icons wide, the signature requests' three-dot menu on the phone */}
                      <RowActions slot="end" isMobile={isMobile} actions={[
                        t.status === "published" && t.documentType && t.documentType !== "blank" && { key: "open", icon: openOutline, label: "Open to fill in", run: () => navigate(GENERATOR_PAGE[t.documentType] || `/app/c/${t.documentType}`) },
                        { key: "edit", icon: createOutline, label: "Edit", run: () => navigate(`/app/templates/edit/${t.id}`) },
                        t.status === "published"
                          ? { key: "unpublish", icon: cloudOfflineOutline, label: "Unpublish", busy: rowBusy === t.id, run: () => unpublishRow(t) }
                          : { key: "publish", icon: cloudUploadOutline, label: "Publish", busy: rowBusy === t.id, run: () => publishRow(t) },
                        { key: "duplicate", icon: copyOutline, label: "Duplicate", run: () => duplicateRow(t) },
                        { key: "delete", icon: trashOutline, label: "Delete", color: "danger", run: () => deleteRow(t) },
                      ]} />
                    </IonItem>
                  );
                })}
              </IonItemGroup>
            </IonList>
          </IonCard>
          )}

          {segment === "categories" && (
            <IonCard className="mods-card">
              <IonCardHeader>
                <IonCardTitle className="type-title"><span>Create Menu</span></IonCardTitle>
              </IonCardHeader>
              <IonList>
                <IonItemGroup>
                  <IonItemDivider className="border-top">
                    <IonLabel className="ion-text-wrap">The toggle shows or hides an item in the Create menu, and dragging reorders it. A category always stays a topbar tab.</IonLabel>
                    <span slot="end" className="mods-count">{menuItems.length}</span>
                  </IonItemDivider>
                  <IonReorderGroup disabled={false} onIonItemReorder={reorderMenu}>
                    {menuItems.map((it, idx) => (
                      <IonItem key={it.id} lines={idx === menuItems.length - 1 ? "none" : undefined} className="mod-row create-menu-row">
                        <IonReorder slot="start" style={{ marginRight: 12 }} />
                        <IonLabel>
                          {it.label}
                          <p>{it.kind === "category" ? `${templateCount(it.id)} template${templateCount(it.id) === 1 ? "" : "s"}` : "Built-in"}</p>
                        </IonLabel>
                        <IonToggle slot="end" checked={it.shown} onIonChange={(e) => toggleMenuItem(it, e.detail.checked)} aria-label="Show in Create menu" style={{ marginRight: 4 }} />
                        {it.kind === "category" && (
                          <IonButton slot="end" fill="clear" color="danger" size="small" className="app-row-icon-btn" style={{ margin: 0 }} disabled={rowBusy === it.id} onClick={() => deleteCategory(it.cat)} aria-label="Delete category">
                            {rowBusy === it.id ? <IonSpinner name="crescent" style={{ width: 16, height: 16 }} /> : <IonIcon slot="icon-only" icon={trashOutline} style={{ fontSize: 18 }} />}
                          </IonButton>
                        )}
                      </IonItem>
                    ))}
                  </IonReorderGroup>
                </IonItemGroup>
              </IonList>
            </IonCard>
          )}
        </div>
      )}

      <FreshPopover
        open={starterMenu.open}
        event={starterMenu.event}
        onClose={() => setStarterMenu({ open: false, event: undefined })}
        side="bottom" alignment="end" style={{ "--width": "288px", "--offset-y": "4px" }}
      >
        <IonContent>
          <IonList lines="none" style={{ padding: "4px 0" }}>
            <div style={{ padding: "8px 14px 4px", fontSize: "0.65rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--ion-color-medium)" }}>
              Start from
            </div>
            {availableStarters.map((st) => (
              <IonItem key={st.key} button detail={false} onClick={() => createFromStarter(st)}
                style={{ "--min-height": "48px", "--padding-start": "14px", "--inner-padding-end": "14px", fontSize: "0.88rem" }}>
                <IonLabel>
                  {st.name}
                  <p style={{ fontSize: "0.72rem", color: "var(--ion-color-medium)", margin: 0 }}>{DOC_TYPE_LABELS[st.documentType] || st.documentType}</p>
                </IonLabel>
              </IonItem>
            ))}
          </IonList>
        </IonContent>
      </FreshPopover>
    </AppLayout>
  );
}
